/**
 * FLUX API client.
 *
 * All requests route through Vite's dev proxy (/api → localhost:8002)
 * or the production FLUX_API_URL environment variable.
 */

const BASE_URL = import.meta.env.VITE_FLUX_API_URL ?? ''
const API_KEY  = import.meta.env.VITE_FLUX_API_KEY  ?? 'dev-flux-key'

const headers = () => ({
  'Content-Type': 'application/json',
  'X-API-Key': API_KEY,
})

async function _request(path, options = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: { ...headers(), ...options.headers },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`FLUX API ${res.status}: ${text.slice(0, 200)}`)
  }
  return res.json()
}

// ── Site assessment ──────────────────────────────────────────────

/**
 * Run a full site assessment.
 * @param {object} params
 * @param {number} params.latitude
 * @param {number} params.longitude
 * @param {string} params.technology   "solar_pv" | "wind"
 * @param {number} params.capacity_mw
 * @param {string} params.country
 */
export async function assessSite(params) {
  return _request('/api/v1/assess', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

// ── Yield simulation ─────────────────────────────────────────────

export async function simulateYield(params) {
  return _request('/api/v1/yield', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

// ── Grid nearest ─────────────────────────────────────────────────

export async function nearestGrid({ lat, lon, capacity_mw = 10, country = 'indonesia' }) {
  const qs = new URLSearchParams({ lat, lon, capacity_mw, country })
  return _request(`/api/v1/grid/nearest?${qs}`)
}

// ── Area scan ────────────────────────────────────────────────────

/**
 * Area scan — returns a GeoJSON FeatureCollection.
 * @param {object} params
 * @param {number} params.min_lat
 * @param {number} params.max_lat
 * @param {number} params.min_lon
 * @param {number} params.max_lon
 * @param {number} [params.resolution_km=10]
 * @param {string} [params.technology="solar_pv"]
 * @param {number} [params.capacity_mw=10]
 * @param {string} [params.country="indonesia"]
 */
export async function areaScan(params) {
  const qs = new URLSearchParams(params)
  return _request(`/api/v1/area/scan?${qs}`)
}

// ── Compare ──────────────────────────────────────────────────────

/**
 * Compare multiple sites.
 * @param {object} params
 * @param {Array<{latitude, longitude, label}>} params.sites
 * @param {string} params.technology
 * @param {number} params.capacity_mw
 * @param {string} params.country
 */
export async function compareSites(params) {
  return _request('/api/v1/compare', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

// ── Health ───────────────────────────────────────────────────────

export async function getHealth() {
  return _request('/api/v1/health')
}

// ── Report ───────────────────────────────────────────────────────

export async function generateReport(assessment, fmt = 'markdown') {
  const res = await fetch(`${BASE_URL}/api/v1/report?fmt=${fmt}`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ assessment }),
  })
  if (!res.ok) throw new Error(`Report API ${res.status}`)
  return fmt === 'markdown' ? res.text() : res.json()
}
